import { type ReactNode, createContext, useContext } from 'react';
import { useStore } from 'zustand';

import { useRef } from 'react';
// import { create, serverOnly, withPageContext } from 'vike-react-zustand';
import { createStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type {
  InfiniteStore,
  NonNullableKeys,
  UseInfiniteRequestItem,
  WritableDraft,
} from '@/renderer/utils/api/types';
import { deepEqual } from 'fast-equals';
import { IS_SERVER } from '../ssr';

//

const ID = 'id';
const DEFAULT_BY = ID;
const DEFAULT_DIR = 'DESC';
const LIMIT = 50;

//

const _noop = () => {
  /* void */
};

// Generate a new instance of a "default new item" for immer's draft purposes when one doesn't exist
const getDefaultNewItem = (): UseInfiniteRequestItem<any, any> => ({
  data: {
    data: [],
    pageInfo: {
      limit: LIMIT,
      total: 0,
      order: {
        by: DEFAULT_BY,
        dir: DEFAULT_DIR,
      },
    },
  },
  error: undefined,
  isEmpty: false,
  isError: false,
  isFetching: false,
  isFetchingNextPage: false,
  isFetchingPreviousPage: false,
  isLoading: false,
  isPending: false,
  isPlaceholderData: false,
  isRefetching: false,
  isSuccess: false,
});

const createInfiniteStore = (initBanks?: Record<string, UseInfiniteRequestItem<any, any>>) =>
  createStore<InfiniteStore<any, any>>()(
    // withPageContext((_pageContext) =>
    subscribeWithSelector(
      immer((set, get) => ({
        banks: initBanks || {},
        get: (key) => get().banks[key]?.data,
        set: (key, cb) => {
          set((state) => {
            // Create a new RequestItem block if it for some reason didn't exist before
            const nextObj = (state.banks[key] || getDefaultNewItem()) as WritableDraft<
              NonNullableKeys<UseInfiniteRequestItem<any, any>, 'data'>
            >;

            cb(nextObj, get()?.banks[key]?.data);

            if (!state.banks[key]) {
              // Only add the new item if we changed it
              if (!deepEqual(nextObj, getDefaultNewItem())) {
                // In this case, we didn't pass a WritableDraft to cb, so we have to add it to the obj ourselves
                state.banks[key] = nextObj;
              }
            } // else: cb directly modified state.banks[key] if it wanted to, so nothing else for us to do
          });
        },
        remove: (key) => {
          set((state) => {
            delete state.banks[key];
          });
        },
      })),
    ),
    // ),
  );

export type InfiniteStoreApi = ReturnType<typeof createInfiniteStore>;

export const InfiniteStoreContext = createContext<InfiniteStoreApi | undefined>(undefined);

export interface InfiniteStoreProviderProps {
  children: ReactNode;
}

export const InfiniteStoreProvider = ({ children }: InfiniteStoreProviderProps) => {
  const storeRef = useRef<InfiniteStoreApi>();
  if (!storeRef.current) {
    if (IS_SERVER) {
      storeRef.current = createInfiniteStore();
    } else {
      // This is a temporary solution until we can get vike-react-zustand
      const initValue: Record<string, UseInfiniteRequestItem<any, any>> = {};

      for (const el of document.querySelectorAll('.react-streaming_initData')) {
        if (el.textContent?.includes('"∞')) {
          try {
            const json = JSON.parse(el.textContent);
            if (json?.key) {
              const keys = JSON.parse(json?.key);

              if (keys?.[0] === '#useRequest#' && keys?.[1]?.startsWith?.('∞')) {
                // This particular type of data was pulled via useRequest (deprecated)
                const infinityKey = keys[1];

                if (json.value?.data && typeof json.value?.data !== 'string') {
                  // Strings are probably !undefined or whatever
                  const obj = json.value;

                  const length = obj.data?.data?.length || 0;
                  // @todo we need to know if this was sorted ASC or DESC... should we put it in the key?
                  const _newestKey = 0;
                  // initialOpts.query?.order?.dir === 'DESC' ? 0 : (length || 1) - 1;
                  const _oldestKey = (length || 1) - 1;
                  // initialOpts.query?.order?.dir === 'DESC' ? (length || 1) - 1 : 0;

                  const error = obj.error && typeof obj.error !== 'string' ? obj.error : undefined;
                  const loading = !!obj.loading && obj.loading !== '!undefined';
                  const pending = !!obj.pending && obj.pending !== '!undefined';

                  initValue[infinityKey] = {
                    data: obj.data,
                    error,
                    isEmpty: length === 0,
                    isError: !!error,
                    isFetching: loading,
                    isFetchingNextPage: false,
                    isFetchingPreviousPage: false,
                    isLoading: loading,
                    // Mark pending if in SSR & we don't run it until client
                    isPending: pending || loading, // || (IS_SERVER && !!opts.serverSkip && !opts.pause),
                    isPlaceholderData: false,
                    isRefetching: false,
                    // Success only when result data is an array, even if it has no items
                    isSuccess:
                      !error && !pending && !loading && obj.data?.data?.length !== undefined,
                  };
                }
              } else if (json.value?.pageInfo) {
                // This data set fetched a paginated API response
                const infinityKey = keys[0];

                // Strings are probably !undefined or whatever
                const obj = json.value as API.WithPage<API.DbItem[]>;

                const length = obj.data?.length || 0;
                // @todo we need to know if this was sorted ASC or DESC... should we put it in the key?
                const _newestKey = 0;
                // initialOpts.query?.order?.dir === 'DESC' ? 0 : (length || 1) - 1;
                const _oldestKey = (length || 1) - 1;
                // initialOpts.query?.order?.dir === 'DESC' ? (length || 1) - 1 : 0;

                const error = undefined;
                const loading = false;
                const pending = false;

                initValue[infinityKey] = {
                  data: obj,
                  error,
                  isEmpty: length === 0,
                  isError: !!error,
                  isFetching: loading,
                  isFetchingNextPage: false,
                  isFetchingPreviousPage: false,
                  isLoading: loading,
                  // Mark pending if in SSR & we don't run it until client
                  isPending: pending || loading, // || (IS_SERVER && !!opts.serverSkip && !opts.pause),
                  isPlaceholderData: false,
                  isRefetching: false,
                  // Success only when result data is an array, even if it has no items
                  isSuccess: !error && !pending && !loading && obj.data?.length !== undefined,
                };
              }
            }
          } catch (_e) {
            // skip
          }
        }
      }

      storeRef.current = createInfiniteStore(initValue);
    }
  }

  return (
    <InfiniteStoreContext.Provider value={storeRef.current}>
      {children}
    </InfiniteStoreContext.Provider>
  );
};

export const useInfiniteStore = <T, ItemType, MetaType>(
  selector: (store: InfiniteStore<ItemType, MetaType>) => T,
): T => {
  const infiniteStoreContext = useContext(InfiniteStoreContext);

  if (!infiniteStoreContext) {
    throw new Error(`useInfiniteStore must be used within InfiniteStoreProvider`);
  }

  return useStore(infiniteStoreContext, selector);
};
